"use client"

import { Toaster as ChakraToaster, Spinner, Stack, Toast, createToaster } from "@chakra-ui/react"

export const toaster = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: false,
})

/**
 * Explicit color map for toast types.
 * Chakra's semantic tokens (e.g. `red.solid`, `red.contrast`) don't resolve
 * inside Shadow DOM because the cssVarsRoot selector doesn't match the shadow
 * structure. Using hardcoded values ensures correct contrast regardless of
 * where the Toaster is portalled.
 */
const toastColors: Record<string, { bg: string; color: string }> = {
  error: { bg: "#E53E3E", color: "white" },
  success: { bg: "#38A169", color: "white" },
  warning: { bg: "#DD6B20", color: "white" },
  // Follows the Colour Mode where the theme's variables reach, else today's light toast
  loading: { bg: "var(--planner-colors-card, white)", color: "var(--planner-colors-primary, #2B2B2B)" },
  // Neutral: something happened that isn't a success or a problem (e.g. changes discarded).
  // The brand blue of the primary buttons (hydrologiq_primary in styling/button.ts)
  info: { bg: "#39aed2", color: "white" },
}

export const Toaster = () => {
  return (
    <ChakraToaster toaster={toaster} insetInline={{ mdDown: "4" }}>
      {(toast) => {
        const colors = toastColors[toast.type ?? "info"] ?? toastColors.info
        return (
          <Toast.Root
            width={{ md: "sm" }}
            style={{
              backgroundColor: colors.bg,
              color: colors.color,
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              borderRadius: "8px",
            }}
          >
            {toast.type === "loading" ? <Spinner size="sm" color="blue.500" /> : <Toast.Indicator />}
            <Stack gap="1" flex="1" maxWidth="100%">
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
            </Stack>
            {toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}
            {/* Every toast can be closed, whatever created it; the ✕ takes the toast's text colour */}
            <Toast.CloseTrigger aria-label="Close notification" style={{ color: "inherit", cursor: "pointer" }} />
          </Toast.Root>
        )
      }}
    </ChakraToaster>
  )
}
